import { Tile, TileContent, TileMedia, TileText } from '@design-system';
import { cn } from '@utils/cn';
import { ReactNode } from 'react';

import { Image } from '../../../components/Image';
import { Icon } from '../Icon/Icon';
import { QuantitySelector } from '../QuantitySelector/QuantitySelector';

export type SelectableTileStatus = 'disabled' | 'entered' | 'error' | 'soldOut';

const STATUS_BADGES: Partial<Record<SelectableTileStatus, string>> = {
  entered: 'Entered',
  soldOut: 'Sold out',
};

export interface SelectableTileProps {
  id: string;
  image?: {
    src: string;
    alt: string;
    width: 16 | 32 | 48 | 64 | 96 | 128 | 256 | 384;
  };
  title: string;
  price: string | ReactNode;
  quantity?: number;
  maxQuantity?: number;
  status?: SelectableTileStatus;
  isSelected: boolean;
  onClick: () => void;
  onQuantityChange?: (quantity: number) => void;
}

export const SelectableTile = ({
  id,
  image,
  isSelected,
  maxQuantity = 10,
  onClick,
  onQuantityChange,
  price,
  quantity,
  status,
  title,
}: SelectableTileProps) => {
  const isDisabled = status === 'disabled';
  const isUnavailable = status === 'entered' || status === 'soldOut';
  const badge = status ? STATUS_BADGES[status] : undefined;
  const isError = status === 'error';
  const selectionLabel = isSelected ? 'Selected' : 'Not Selected';

  const showQuantitySelector =
    isSelected && quantity !== undefined && quantity > 0 && onQuantityChange;

  return (
    <div
      className={cn(
        'relative flex w-full flex-col gap-4 rounded-sm border border-divider/20 transition-colors',
        isSelected && 'border-neutral-6 ring-1 ring-neutral-6',
        isDisabled && 'border-neutral-4 opacity-30',
        isError && 'border-danger-4',
        showQuantitySelector && 'pb-4',
        {
          'hover:border-divider/40':
            !isSelected && !isDisabled && !isUnavailable && !isError,
        },
      )}
    >
      <button
        aria-label={`${title} - ${badge ?? selectionLabel}`}
        className={showQuantitySelector ? 'px-4 pt-4' : 'p-4'}
        disabled={isDisabled || isUnavailable}
        onClick={onClick}
        type="button"
      >
        <Tile key={id} className="group" direction="column">
          {badge && (
            <span className="absolute right-0 top-0 z-10 m-2 flex items-center justify-center rounded-full bg-white px-2 py-1.5 text-xs ring-1 ring-divider/20">
              {badge}
            </span>
          )}
          {isSelected && (
            <>
              {quantity && !onQuantityChange ? (
                <span className="absolute right-0 top-0 z-10 m-2 flex h-6 w-6 items-center justify-center rounded-full bg-neutral-6 text-xs text-white">
                  {quantity}
                </span>
              ) : (
                <Icon
                  size="md"
                  className="absolute right-0 top-0 z-10 m-2 rounded-full bg-neutral-6 text-white"
                  name="ic_check"
                />
              )}
            </>
          )}
          {image && (
            <TileMedia className={cn(isUnavailable && 'opacity-30')}>
              <Image
                className="bg-[#F3F3F3]"
                shape="rounded"
                src={image.src}
                alt={image.alt}
                width={image.width}
                objectFit="contain"
              />
            </TileMedia>
          )}
          <TileContent
            direction="column"
            className={cn('gap-4', isUnavailable && 'opacity-30')}
          >
            <TileText className="flex flex-col">
              <span>{title}</span>
              <TileText variant="muted">{price}</TileText>
            </TileText>
          </TileContent>
        </Tile>
      </button>
      {showQuantitySelector && (
        <div
          className="mt-auto w-full px-4"
          onClick={(e) => e.stopPropagation()}
        >
          <QuantitySelector
            quantity={quantity}
            minQuantity={1}
            maxQuantity={maxQuantity}
            onChange={onQuantityChange}
            className="w-full justify-between"
          />
        </div>
      )}
    </div>
  );
};
